import { useCallback, useEffect, useState } from 'react';

import { getDefaultLanguage, getLanguageContent } from '../api/languages';
import { fetchPlace } from '../api/place';
import BaseHead from '../components/BaseHead';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import ComponentType from '../types/Component';
import PlaceType from '../types/Place';
import { PlaceLanguagesType } from '../types/languages';

import BookingStorageProvider from './BookingStorage';
import CustomerInfoProvider from './CustomerInfo';
import DeviceTypeProvider from './DeviceType';
import { EventSourceProvider } from './EventSource';
import { LocaleProvider } from './Locale';
import PlaceProvider from './Place';
import StylesProvider from './Styles';
import ThemeProvider from './Theme';
import { DEFAULT_THEME } from './Theme/types';

type RootProviderType = {};

type RootStateProvider = {
	language: PlaceLanguagesType;
	place: PlaceType | null;
	placeError: string | null;
};

const RootProvider: ComponentType<RootProviderType> = ({ children }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [rootProvider, setRootProvider] = useState<RootStateProvider>();

	const fetchInitialDataAsync = useCallback(async () => {
		const defaultLanguage = getDefaultLanguage();
		const [langContent, place] = await Promise.all([
			getLanguageContent(),
			fetchPlace(defaultLanguage),
		]);

		setRootProvider({
			language: {
				current: defaultLanguage,
				content: langContent,
			},
			place: place.result,
			placeError: place.error?.message || null,
		});
		setIsLoading(false);
	}, []);

	useEffect(() => {
		fetchInitialDataAsync();
	}, []);

	if (isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<DeviceTypeProvider>
			<LocaleProvider
				language={rootProvider?.language?.current!}
				initialState={rootProvider?.language!}>
				<PlaceProvider initialState={rootProvider?.place!} error={rootProvider?.placeError || null}>
					<ThemeProvider theme={rootProvider?.place?.styles?.theme ?? DEFAULT_THEME}>
						<StylesProvider initialState={rootProvider?.place?.styles!}>
							<BaseHead />
							<BookingStorageProvider>
								<EventSourceProvider>
									<CustomerInfoProvider>{children}</CustomerInfoProvider>
								</EventSourceProvider>
							</BookingStorageProvider>
						</StylesProvider>
					</ThemeProvider>
				</PlaceProvider>
			</LocaleProvider>
		</DeviceTypeProvider>
	);
};

export default RootProvider;
